<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-clipboard-pulse-outline me-2"></i>{{ $t('page.specialty', 'Specialty Disease Registries') }}</h4>
        </div>
        <button class="hms-btn hms-btn-blue" @click="loadAll"><i class="mdi mdi-refresh"></i></button>
      </div>

      <!-- TOP TABS — disease selector -->
      <div class="hms-disease-bar">
        <button class="hms-disease-btn" :class="{ active: disease==='dm' }" @click="disease='dm'; loadDm()">
          <i class="mdi mdi-water-outline"></i>
          <div>Diabetes</div>
          <span class="hms-disease-count">{{ dm.stats.active || 0 }}</span>
        </button>
        <button class="hms-disease-btn" :class="{ active: disease==='ca' }" @click="disease='ca'; loadCa()">
          <i class="mdi mdi-ribbon"></i>
          <div>Cancer</div>
          <span class="hms-disease-count">{{ ca.stats.active || 0 }}</span>
        </button>
        <button class="hms-disease-btn" :class="{ active: disease==='dial' }" @click="disease='dial'; loadDial()">
          <i class="mdi mdi-water-pump"></i>
          <div>Dialysis</div>
          <span class="hms-disease-count">{{ dial.stats.active || 0 }}</span>
        </button>
        <button class="hms-disease-btn" :class="{ active: disease==='nut' }" @click="disease='nut'; loadNut()">
          <i class="mdi mdi-food-apple"></i>
          <div>Nutrition (IMAM)</div>
          <span class="hms-disease-count">{{ nut.stats.active || 0 }}</span>
        </button>
      </div>

      <!-- ============ DIABETES ============ -->
      <div v-if="disease==='dm'">
        <div class="hms-stat-row">
          <div class="hms-stat"><div class="hms-stat-num hms-stat-info">{{ dm.stats.active || 0 }}</div><div class="hms-stat-lbl">Active</div></div>
          <div class="hms-stat"><div class="hms-stat-num hms-stat-success">{{ dm.stats.controlled || 0 }}</div><div class="hms-stat-lbl">Controlled</div></div>
          <div class="hms-stat"><div class="hms-stat-num hms-stat-danger">{{ dm.stats.uncontrolled || 0 }}</div><div class="hms-stat-lbl">Uncontrolled</div></div>
          <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ dm.stats.hba1c_above_7 || 0 }}</div><div class="hms-stat-lbl">HbA1c ≥7</div></div>
          <div class="hms-stat"><div class="hms-stat-num">{{ dm.stats.avg_hba1c != null ? dm.stats.avg_hba1c + '%' : '—' }}</div><div class="hms-stat-lbl">Avg HbA1c</div></div>
        </div>
        <div class="d-flex justify-content-between mb-2">
          <input v-model="dm.search" class="hms-input" placeholder="Search DM client" @keyup.enter="loadDm" style="max-width:280px" />
          <button class="hms-btn hms-btn-success" @click="openEnrolDm()"><i class="mdi mdi-account-plus"></i> Enrol DM client</button>
        </div>
        <table class="hms-table">
          <thead><tr><th>DM #</th><th>Patient</th><th>Type</th><th>HbA1c</th><th>FBS</th><th>Retina</th><th>Foot risk</th><th>Next visit</th><th>Status</th><th></th></tr></thead>
          <tbody>
            <tr v-for="c in dm.clients" :key="c.diabetes_id" :class="dmRow(c)">
              <td><strong>{{ c.diabetes_number }}</strong></td>
              <td>{{ c.patient_name }}<div class="hms-muted-sm">{{ c.patient_no }}</div></td>
              <td><span class="hms-pill hms-pill-info">{{ c.diabetes_type }}</span></td>
              <td :class="hba1cClass(c.last_hba1c)">{{ c.last_hba1c != null ? c.last_hba1c + '%' : '—' }}</td>
              <td>{{ c.last_fasting_sugar != null ? c.last_fasting_sugar : '—' }}</td>
              <td><span class="hms-pill" :class="screenPill(c.retinopathy_screen)">{{ c.retinopathy_screen }}</span></td>
              <td><span class="hms-pill" :class="footPill(c.foot_risk)">{{ c.foot_risk || '—' }}</span></td>
              <td :class="c.days_to_appointment != null && c.days_to_appointment < 0 ? 'st-bad' : ''">{{ c.next_visit_date || '—' }}</td>
              <td><span class="hms-pill" :class="dmStatusPill(c.status)">{{ c.status }}</span></td>
              <td><button class="hms-btn hms-btn-blue hms-btn-sm" @click="openEditDm(c)"><i class="mdi mdi-pencil"></i></button></td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ============ CANCER ============ -->
      <div v-if="disease==='ca'">
        <div class="hms-stat-row">
          <div class="hms-stat"><div class="hms-stat-num hms-stat-info">{{ ca.stats.active || 0 }}</div><div class="hms-stat-lbl">Active</div></div>
          <div class="hms-stat"><div class="hms-stat-num hms-stat-success">{{ ca.stats.remission || 0 }}</div><div class="hms-stat-lbl">Remission</div></div>
          <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ ca.stats.palliative || 0 }}</div><div class="hms-stat-lbl">Palliative</div></div>
          <div class="hms-stat"><div class="hms-stat-num hms-stat-danger">{{ ca.stats.stage4 || 0 }}</div><div class="hms-stat-lbl">Stage IV</div></div>
          <div class="hms-stat"><div class="hms-stat-num">{{ ca.stats.new_quarter || 0 }}</div><div class="hms-stat-lbl">New (90d)</div></div>
        </div>
        <div class="d-flex justify-content-between mb-2">
          <input v-model="ca.search" class="hms-input" placeholder="Search by name / cancer type" @keyup.enter="loadCa" style="max-width:280px" />
          <button class="hms-btn hms-btn-success" @click="openEnrolCa()"><i class="mdi mdi-plus"></i> Register cancer case</button>
        </div>
        <table class="hms-table">
          <thead><tr><th>CA #</th><th>Patient</th><th>Cancer type</th><th>Site</th><th>Stage</th><th>Modality</th><th>Oncologist</th><th>Next appt</th><th>Status</th><th></th></tr></thead>
          <tbody>
            <tr v-for="c in ca.clients" :key="c.cancer_id" :class="caRow(c)">
              <td><strong>{{ c.cancer_number }}</strong></td>
              <td>{{ c.patient_name }}<div class="hms-muted-sm">{{ c.patient_no }}</div></td>
              <td>{{ c.cancer_type }}<div class="hms-muted-sm">{{ c.icd_code }}</div></td>
              <td>{{ c.primary_site || '—' }}<span v-if="c.laterality && c.laterality !== 'not_applicable'" class="hms-muted-sm"> ({{ c.laterality }})</span></td>
              <td><span class="hms-pill" :class="stagePill(c.stage)">{{ c.stage || '—' }}</span></td>
              <td class="hms-muted-sm">{{ c.treatment_modality || '—' }}</td>
              <td>{{ c.oncologist_name || '—' }}</td>
              <td :class="c.days_to_appointment != null && c.days_to_appointment < 0 ? 'st-bad' : ''">{{ c.next_appointment || '—' }}</td>
              <td><span class="hms-pill" :class="caStatusPill(c.status)">{{ c.status }}</span></td>
              <td><button class="hms-btn hms-btn-blue hms-btn-sm" @click="openEditCa(c)"><i class="mdi mdi-pencil"></i></button></td>
            </tr>
          </tbody>
        </table>
        <div v-if="ca.topTypes && ca.topTypes.length" class="hms-card mt-3">
          <div class="hms-card-head">Top cancer types</div>
          <div class="hms-card-body p-0">
            <table class="hms-table">
              <thead><tr><th>#</th><th>Cancer type</th><th>Cases</th></tr></thead>
              <tbody>
                <tr v-for="(t, i) in ca.topTypes" :key="i">
                  <td>{{ i + 1 }}</td>
                  <td>{{ t.cancer_type }}</td>
                  <td><strong>{{ t.n }}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- ============ DIALYSIS ============ -->
      <div v-if="disease==='dial'">
        <div class="hms-stat-row">
          <div class="hms-stat"><div class="hms-stat-num hms-stat-info">{{ dial.stats.active || 0 }}</div><div class="hms-stat-lbl">Active</div></div>
          <div class="hms-stat"><div class="hms-stat-num">{{ dial.stats.hd || 0 }}</div><div class="hms-stat-lbl">HD</div></div>
          <div class="hms-stat"><div class="hms-stat-num">{{ dial.stats.pd || 0 }}</div><div class="hms-stat-lbl">PD / CAPD</div></div>
          <div class="hms-stat"><div class="hms-stat-num hms-stat-success">{{ dial.stats.sessions_today || 0 }}</div><div class="hms-stat-lbl">Sessions today</div></div>
          <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ dial.stats.missed_scheduled || 0 }}</div><div class="hms-stat-lbl">Missed sessions</div></div>
        </div>
        <div class="d-flex justify-content-between mb-2">
          <input v-model="dial.search" class="hms-input" placeholder="Search dialysis client" @keyup.enter="loadDial" style="max-width:280px" />
          <button class="hms-btn hms-btn-success" @click="openEnrolDial()"><i class="mdi mdi-account-plus"></i> Enrol dialysis client</button>
        </div>
        <table class="hms-table">
          <thead><tr><th>Dial #</th><th>Patient</th><th>CKD</th><th>Type</th><th>Access</th><th>Sess/wk</th><th>Sessions done</th><th>Next</th><th></th></tr></thead>
          <tbody>
            <tr v-for="c in dial.clients" :key="c.dialysis_id" :class="c.days_to_session != null && c.days_to_session < 0 ? 'row-warn' : ''">
              <td><strong>{{ c.dialysis_number }}</strong></td>
              <td>{{ c.patient_name }}<div class="hms-muted-sm">{{ c.patient_no }}</div></td>
              <td>Stage {{ c.ckd_stage }}</td>
              <td><span class="hms-pill hms-pill-info">{{ c.dialysis_type }}</span></td>
              <td class="hms-muted-sm">{{ c.access_type || '—' }}</td>
              <td>{{ c.sessions_per_week }}</td>
              <td>{{ c.sessions_done }}</td>
              <td :class="c.days_to_session != null && c.days_to_session < 0 ? 'st-bad' : ''">{{ c.next_session_date || '—' }}</td>
              <td><button class="hms-btn hms-btn-blue hms-btn-sm" @click="openDialDetail(c)"><i class="mdi mdi-folder-open"></i></button></td>
            </tr>
          </tbody>
        </table>

        <div v-if="dial.detail.client" class="hms-card mt-3">
          <div class="hms-card-head d-flex justify-content-between">
            <span>{{ dial.detail.client.dialysis_number }} — {{ dial.detail.client.patient_name }}</span>
            <div class="d-flex gap-2">
              <button class="hms-btn hms-btn-success hms-btn-sm" @click="openSessionModal()"><i class="mdi mdi-plus"></i> Record session</button>
              <button class="hms-btn hms-btn-secondary hms-btn-sm" @click="dial.detail.client = null">Close</button>
            </div>
          </div>
          <div class="hms-card-body p-0">
            <table v-if="dial.detail.sessions.length" class="hms-table">
              <thead><tr><th>Date</th><th>Duration</th><th>Pre wt</th><th>Post wt</th><th>Fluid removed</th><th>Nurse</th><th>Status</th><th>Complications</th></tr></thead>
              <tbody>
                <tr v-for="s in dial.detail.sessions" :key="s.session_id">
                  <td>{{ s.session_date }} <span class="hms-muted-sm">{{ s.start_time ? s.start_time.substring(0,5) : '' }}</span></td>
                  <td>{{ s.duration_minutes ? s.duration_minutes + 'm' : '—' }}</td>
                  <td>{{ s.pre_weight || '—' }}</td>
                  <td>{{ s.post_weight || '—' }}</td>
                  <td>{{ s.fluid_removed ? s.fluid_removed + 'L' : '—' }}</td>
                  <td>{{ s.nurse_name || '—' }}</td>
                  <td><span class="hms-pill" :class="sessStatusPill(s.status)">{{ s.status }}</span></td>
                  <td class="hms-muted-sm">{{ s.complications || '' }}</td>
                </tr>
              </tbody>
            </table>
            <div v-else class="hms-pad text-center hms-muted">No sessions recorded.</div>
          </div>
        </div>
      </div>

      <!-- ============ NUTRITION ============ -->
      <div v-if="disease==='nut'">
        <div class="hms-stat-row">
          <div class="hms-stat"><div class="hms-stat-num hms-stat-info">{{ nut.stats.active || 0 }}</div><div class="hms-stat-lbl">Active</div></div>
          <div class="hms-stat"><div class="hms-stat-num hms-stat-danger">{{ nut.stats.sam || 0 }}</div><div class="hms-stat-lbl">SAM</div></div>
          <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ nut.stats.mam || 0 }}</div><div class="hms-stat-lbl">MAM</div></div>
          <div class="hms-stat"><div class="hms-stat-num hms-stat-success">{{ nut.stats.recovered || 0 }}</div><div class="hms-stat-lbl">Recovered</div></div>
          <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ nut.stats.defaulted || 0 }}</div><div class="hms-stat-lbl">Defaulted</div></div>
        </div>
        <div class="d-flex justify-content-between mb-2">
          <input v-model="nut.search" class="hms-input" placeholder="Search nutrition client" @keyup.enter="loadNut" style="max-width:280px" />
          <button class="hms-btn hms-btn-success" @click="openEnrolNut()"><i class="mdi mdi-plus"></i> Enrol IMAM client</button>
        </div>
        <table class="hms-table">
          <thead><tr><th>NUT #</th><th>Patient</th><th>Age</th><th>Type</th><th>MUAC</th><th>WHZ</th><th>Oedema</th><th>RUTF</th><th>Next visit</th><th>Status</th><th></th></tr></thead>
          <tbody>
            <tr v-for="c in nut.clients" :key="c.nutrition_id" :class="nutRow(c)">
              <td><strong>{{ c.nutrition_number }}</strong></td>
              <td>{{ c.patient_name }}<div class="hms-muted-sm">{{ c.patient_no }}</div></td>
              <td>{{ c.age_months != null ? c.age_months + 'mo' : '—' }}</td>
              <td><span class="hms-pill" :class="admissionPill(c.admission_type)">{{ c.admission_type }}</span></td>
              <td :class="muacClass(c.muac)">{{ c.muac != null ? c.muac + 'cm' : '—' }}</td>
              <td>{{ c.whz_score != null ? c.whz_score : '—' }}</td>
              <td>{{ c.oedema == 1 ? 'Yes' : '—' }}</td>
              <td>{{ c.rutf_amount ? c.rutf_amount + ' ' + (c.rutf_unit || 'sachets') : '—' }}</td>
              <td :class="c.days_to_appointment != null && c.days_to_appointment < 0 ? 'st-bad' : ''">{{ c.next_visit_date || '—' }}</td>
              <td><span class="hms-pill" :class="nutStatusPill(c.status, c.exit_type)">{{ c.exit_type || c.status }}</span></td>
              <td><button class="hms-btn hms-btn-blue hms-btn-sm" @click="openEditNut(c)"><i class="mdi mdi-pencil"></i></button></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ===== ENROL / EDIT MODALS (one per disease) ===== -->
    <div v-if="modal" class="hms-modal-bg" @click.self="modal=false">
      <div class="hms-modal" style="width:760px">
        <div class="hms-modal-head">
          <h5>{{ modalTitle }}</h5>
          <button class="hms-icon-btn" @click="modal=false"><i class="mdi mdi-close"></i></button>
        </div>
        <div class="hms-modal-body">
          <div v-if="!form.patient_id && modalMode === 'enrol'">
            <label class="hms-label">Patient *</label>
            <div class="d-flex gap-2">
              <input v-model="form.patient_search" class="hms-input" placeholder="Name / patient #" @keyup.enter="findPatient" />
              <button class="hms-btn hms-btn-blue" @click="findPatient">Find</button>
            </div>
          </div>
          <div v-else-if="form.patient_id || modalMode === 'edit'" class="hms-patient-chip">
            <i class="mdi mdi-account"></i> {{ form.patient_name || form.patient_id }}
            <a v-if="modalMode === 'enrol'" href="javascript:void(0)" class="ms-2" @click="form.patient_id=null">change</a>
          </div>

          <!-- DIABETES FORM -->
          <div v-if="formType === 'dm'" class="row g-2 mt-2">
            <div class="col-4"><label class="hms-label">Type *</label>
              <select v-model="form.diabetes_type" class="hms-input">
                <option value="type1">Type 1</option><option value="type2">Type 2</option>
                <option value="gestational">Gestational</option><option value="mody">MODY</option><option value="other">Other</option>
              </select>
            </div>
            <div class="col-4"><label class="hms-label">Dx date</label><input v-model="form.diagnosis_date" type="date" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Status</label>
              <select v-model="form.status" class="hms-input"><option value="active">Active</option><option value="controlled">Controlled</option><option value="uncontrolled">Uncontrolled</option><option value="lost">Lost</option><option value="deceased">Deceased</option><option value="transferred">Transferred</option></select>
            </div>
            <div class="col-12"><label class="hms-label">Current regimen</label><textarea v-model="form.current_regimen" class="hms-input" rows="2" placeholder="Metformin 500mg BD, Insulin 30u OD…"></textarea></div>
            <div class="col-3"><label class="hms-label">HbA1c %</label><input v-model.number="form.last_hba1c" type="number" step="0.1" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">HbA1c date</label><input v-model="form.last_hba1c_date" type="date" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Fasting BS</label><input v-model.number="form.last_fasting_sugar" type="number" step="0.1" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Foot risk</label>
              <select v-model="form.foot_risk" class="hms-input"><option value="">—</option><option value="low">Low</option><option value="moderate">Moderate</option><option value="high">High</option></select>
            </div>
            <div class="col-4"><label class="hms-label">Retinopathy</label>
              <select v-model="form.retinopathy_screen" class="hms-input"><option value="not_done">Not done</option><option value="normal">Normal</option><option value="mild">Mild</option><option value="moderate">Moderate</option><option value="severe">Severe</option><option value="proliferative">Proliferative</option></select>
            </div>
            <div class="col-4"><label class="hms-label">Nephropathy</label>
              <select v-model="form.nephropathy_screen" class="hms-input"><option value="not_done">Not done</option><option value="normal">Normal</option><option value="microalbuminuria">Microalbuminuria</option><option value="macroalbuminuria">Macroalbuminuria</option><option value="ckd">CKD</option></select>
            </div>
            <div class="col-4"><label class="hms-label">Neuropathy</label>
              <select v-model="form.neuropathy_screen" class="hms-input"><option value="not_done">Not done</option><option value="normal">Normal</option><option value="mild">Mild</option><option value="moderate">Moderate</option><option value="severe">Severe</option></select>
            </div>
            <div class="col-4"><label class="hms-label">Last visit</label><input v-model="form.last_visit_date" type="date" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Next visit</label><input v-model="form.next_visit_date" type="date" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Foot exam date</label><input v-model="form.foot_exam_date" type="date" class="hms-input" /></div>
          </div>

          <!-- CANCER FORM -->
          <div v-if="formType === 'ca'" class="row g-2 mt-2">
            <div class="col-8"><label class="hms-label">Cancer type *</label><input v-model="form.cancer_type" class="hms-input" placeholder="e.g. Breast cancer, Cervical cancer" /></div>
            <div class="col-4"><label class="hms-label">ICD-11</label><input v-model="form.icd_code" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Primary site</label><input v-model="form.primary_site" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Laterality</label>
              <select v-model="form.laterality" class="hms-input"><option value="">—</option><option value="left">Left</option><option value="right">Right</option><option value="bilateral">Bilateral</option><option value="not_applicable">N/A</option></select>
            </div>
            <div class="col-3"><label class="hms-label">Grade</label>
              <select v-model="form.grade" class="hms-input"><option value="">—</option><option value="1">1</option><option value="2">2</option><option value="3">3</option><option value="4">4</option><option value="unknown">Unknown</option></select>
            </div>
            <div class="col-12"><label class="hms-label">Histology</label><input v-model="form.histology" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">T</label><input v-model="form.tnm_t" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">N</label><input v-model="form.tnm_n" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">M</label><input v-model="form.tnm_m" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Stage</label>
              <select v-model="form.stage" class="hms-input"><option value="">—</option><option value="0">0</option><option value="I">I</option><option value="II">II</option><option value="III">III</option><option value="IV">IV</option><option value="unknown">Unknown</option></select>
            </div>
            <div class="col-3"><label class="hms-label">Diagnosis</label><input v-model="form.diagnosis_date" type="date" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Treatment modality (comma-list)</label>
              <input v-model="form.treatment_modality" class="hms-input" placeholder="surgery,chemotherapy,radiotherapy" />
            </div>
            <div class="col-4"><label class="hms-label">Oncologist (user ID)</label><input v-model.number="form.oncologist_id" type="number" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Last visit</label><input v-model="form.last_visit_date" type="date" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Next appt</label><input v-model="form.next_appointment" type="date" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Status</label>
              <select v-model="form.status" class="hms-input"><option value="active">Active</option><option value="remission">Remission</option><option value="recurrence">Recurrence</option><option value="palliative">Palliative</option><option value="deceased">Deceased</option><option value="lost">Lost</option></select>
            </div>
          </div>

          <!-- DIALYSIS FORM -->
          <div v-if="formType === 'dial'" class="row g-2 mt-2">
            <div class="col-3"><label class="hms-label">CKD stage</label>
              <select v-model="form.ckd_stage" class="hms-input"><option value="1">1</option><option value="2">2</option><option value="3a">3a</option><option value="3b">3b</option><option value="4">4</option><option value="5">5</option></select>
            </div>
            <div class="col-3"><label class="hms-label">Dialysis type</label>
              <select v-model="form.dialysis_type" class="hms-input"><option value="haemodialysis">Haemodialysis</option><option value="peritoneal">Peritoneal</option><option value="capd">CAPD</option></select>
            </div>
            <div class="col-3"><label class="hms-label">Sessions/wk</label><input v-model.number="form.sessions_per_week" type="number" min="1" max="7" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Dry weight (kg)</label><input v-model.number="form.dry_weight" type="number" step="0.1" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Access type</label><input v-model="form.access_type" class="hms-input" placeholder="AV Fistula left arm / Tenckhoff catheter / Permcath right IJV" /></div>
            <div class="col-12"><label class="hms-label">Primary cause</label><input v-model="form.primary_cause" class="hms-input" placeholder="Diabetic nephropathy / hypertensive / glomerulonephritis…" /></div>
            <div class="col-6"><label class="hms-label">Last session</label><input v-model="form.last_session_date" type="date" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Next session</label><input v-model="form.next_session_date" type="date" class="hms-input" /></div>
          </div>

          <!-- NUTRITION FORM -->
          <div v-if="formType === 'nut'" class="row g-2 mt-2">
            <div class="col-4"><label class="hms-label">Admission type *</label>
              <select v-model="form.admission_type" class="hms-input"><option value="sam">SAM</option><option value="mam">MAM</option><option value="at_risk">At risk</option></select>
            </div>
            <div class="col-4"><label class="hms-label">MUAC (cm)</label><input v-model.number="form.muac" type="number" step="0.1" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Weight (kg)</label><input v-model.number="form.weight" type="number" step="0.1" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Height (cm)</label><input v-model.number="form.height" type="number" step="0.1" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">WHZ score</label><input v-model.number="form.whz_score" type="number" step="0.1" class="hms-input" /></div>
            <div class="col-4 d-flex align-items-end"><label><input type="checkbox" v-model="form.oedema" :true-value="1" :false-value="0" /> Oedema</label></div>
            <div class="col-12"><label class="hms-label">Complications</label><textarea v-model="form.complications" class="hms-input" rows="2"></textarea></div>
            <div class="col-6"><label class="hms-label">RUTF amount</label><input v-model.number="form.rutf_amount" type="number" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">RUTF unit</label><input v-model="form.rutf_unit" class="hms-input" placeholder="sachets / kg" /></div>
            <div class="col-4"><label class="hms-label">Enrolled</label><input v-model="form.enrollment_date" type="date" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Last visit</label><input v-model="form.last_visit_date" type="date" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Next visit</label><input v-model="form.next_visit_date" type="date" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Exit type</label>
              <select v-model="form.exit_type" class="hms-input"><option value="">—</option><option value="recovered">Recovered</option><option value="died">Died</option><option value="defaulted">Defaulted</option><option value="non_recovered">Non-recovered</option><option value="transferred">Transferred</option></select>
            </div>
            <div class="col-6"><label class="hms-label">Exit date</label><input v-model="form.exit_date" type="date" class="hms-input" /></div>
          </div>

        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="modal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveModal">{{ saving?'Saving…':'Save' }}</button>
        </div>
      </div>
    </div>

    <!-- ===== DIALYSIS SESSION MODAL ===== -->
    <div v-if="sessionModal" class="hms-modal-bg" @click.self="sessionModal=false">
      <div class="hms-modal" style="width:560px">
        <div class="hms-modal-head"><h5>Record dialysis session</h5><button class="hms-icon-btn" @click="sessionModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-4"><label class="hms-label">Date *</label><input v-model="session.session_date" type="date" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Start</label><input v-model="session.start_time" type="time" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">End</label><input v-model="session.end_time" type="time" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Duration (min)</label><input v-model.number="session.duration_minutes" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Pre wt</label><input v-model.number="session.pre_weight" type="number" step="0.01" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Post wt</label><input v-model.number="session.post_weight" type="number" step="0.01" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Fluid removed (L)</label><input v-model.number="session.fluid_removed" type="number" step="0.01" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Nurse (user ID)</label><input v-model.number="session.nurse_id" type="number" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Status</label>
              <select v-model="session.status" class="hms-input"><option value="completed">Completed</option><option value="scheduled">Scheduled</option><option value="in_progress">In progress</option><option value="missed">Missed</option><option value="cancelled">Cancelled</option></select>
            </div>
            <div class="col-12"><label class="hms-label">Complications</label><textarea v-model="session.complications" class="hms-input" rows="2"></textarea></div>
            <div class="col-12"><label class="hms-label">Next session date</label><input v-model="session.next_session_date" type="date" class="hms-input" /></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="sessionModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveSession">{{ saving?'Saving…':'Save' }}</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "SpecialtyRegistries",
  components: { Navigation },
  data() {
    return {
      disease: 'dm', saving: false,
      dm:   { clients: [], stats: {}, search: '' },
      ca:   { clients: [], stats: {}, topTypes: [], search: '' },
      dial: { clients: [], stats: {}, search: '', detail: { client: null, sessions: [] } },
      nut:  { clients: [], stats: {}, search: '' },

      modal: false, modalMode: 'enrol', modalTitle: '', formType: '', form: {},
      sessionModal: false, session: this.blankSession()
    };
  },
  mounted() { this.loadAll(); },
  methods: {
    async loadAll() { await this.loadDm(); await this.loadCa(); await this.loadDial(); await this.loadNut(); },
    async loadDm() {
      try { const r = await executeJson({ function: 'getDiabetesClients', search: this.dm.search }, hms);
        if (r.data && r.data.success) { const d = r.data.data || {}; this.dm.clients = d.Clients || []; this.dm.stats = d.Stats || {}; }
      } catch (e) { console.error(e); }
    },
    async loadCa() {
      try { const r = await executeJson({ function: 'getCancerClients', search: this.ca.search }, hms);
        if (r.data && r.data.success) { const d = r.data.data || {}; this.ca.clients = d.Clients || []; this.ca.stats = d.Stats || {}; this.ca.topTypes = d.TopTypes || []; }
      } catch (e) { console.error(e); }
    },
    async loadDial() {
      try { const r = await executeJson({ function: 'getDialysisClients', search: this.dial.search }, hms);
        if (r.data && r.data.success) { const d = r.data.data || {}; this.dial.clients = d.Clients || []; this.dial.stats = d.Stats || {}; }
      } catch (e) { console.error(e); }
    },
    async loadNut() {
      try { const r = await executeJson({ function: 'getNutritionClients', search: this.nut.search }, hms);
        if (r.data && r.data.success) { const d = r.data.data || {}; this.nut.clients = d.Clients || []; this.nut.stats = d.Stats || {}; }
      } catch (e) { console.error(e); }
    },

    // ---- modal helpers ----
    openEnrolDm()  { this.formType = 'dm';   this.modalMode = 'enrol'; this.modalTitle = 'Enrol Diabetes Client';   this.form = { patient_id: null, patient_name: '', patient_search: '', diabetes_type: 'type2', diagnosis_date: '', current_regimen: '', last_hba1c: null, last_hba1c_date: '', last_fasting_sugar: null, retinopathy_screen: 'not_done', nephropathy_screen: 'not_done', neuropathy_screen: 'not_done', foot_exam_date: '', foot_risk: '', last_visit_date: '', next_visit_date: '', status: 'active' }; this.modal = true; },
    openEditDm(c)  { this.formType = 'dm';   this.modalMode = 'edit';  this.modalTitle = 'Edit DM ' + c.diabetes_number; this.form = { ...c, patient_name: c.patient_name }; this.modal = true; },
    openEnrolCa()  { this.formType = 'ca';   this.modalMode = 'enrol'; this.modalTitle = 'Register Cancer Case';     this.form = { patient_id: null, patient_name: '', patient_search: '', cancer_type: '', icd_code: '', primary_site: '', laterality: '', histology: '', grade: '', tnm_t: '', tnm_n: '', tnm_m: '', stage: '', diagnosis_date: '', treatment_modality: '', oncologist_id: null, last_visit_date: '', next_appointment: '', status: 'active' }; this.modal = true; },
    openEditCa(c)  { this.formType = 'ca';   this.modalMode = 'edit';  this.modalTitle = 'Edit ' + c.cancer_number;  this.form = { ...c, patient_name: c.patient_name }; this.modal = true; },
    openEnrolDial(){ this.formType = 'dial'; this.modalMode = 'enrol'; this.modalTitle = 'Enrol Dialysis Client';    this.form = { patient_id: null, patient_name: '', patient_search: '', ckd_stage: '5', dialysis_type: 'haemodialysis', access_type: '', sessions_per_week: 3, dry_weight: null, primary_cause: '', last_session_date: '', next_session_date: '' }; this.modal = true; },
    openEnrolNut() { this.formType = 'nut';  this.modalMode = 'enrol'; this.modalTitle = 'Enrol IMAM Client';        this.form = { patient_id: null, patient_name: '', patient_search: '', admission_type: 'sam', muac: null, weight: null, height: null, whz_score: null, oedema: 0, complications: '', rutf_amount: null, rutf_unit: 'sachets', enrollment_date: new Date().toISOString().substring(0,10), last_visit_date: '', next_visit_date: '', exit_type: '', exit_date: '' }; this.modal = true; },
    openEditNut(c) { this.formType = 'nut';  this.modalMode = 'edit';  this.modalTitle = 'Edit ' + c.nutrition_number; this.form = { ...c, patient_name: c.patient_name }; this.modal = true; },

    async findPatient() {
      const q = (this.form.patient_search || '').trim();
      if (!q) return;
      const r = await executeJson({ function: 'getPatients', search: q, limit: 1 }, hms);
      if (r.data && r.data.success) {
        const p = ((r.data.data && (r.data.data.Patients || r.data.data.patients)) || [])[0];
        if (!p) { alert('No patient found.'); return; }
        this.form.patient_id = p.patient_id;
        this.form.patient_name = p.first_name + ' ' + p.last_name;
      }
    },

    async saveModal() {
      this.saving = true;
      try {
        let fn;
        if (this.formType === 'dm')   fn = this.modalMode === 'enrol' ? 'enrolDiabetesClient'  : 'updateDiabetesClient';
        if (this.formType === 'ca')   fn = this.modalMode === 'enrol' ? 'enrolCancerClient'    : 'updateCancerClient';
        if (this.formType === 'dial') fn = 'enrolDialysisClient';
        if (this.formType === 'nut')  fn = this.modalMode === 'enrol' ? 'enrolNutritionClient' : 'updateNutritionClient';
        const params = { function: fn, ...this.form };
        delete params.patient_search; delete params.patient_name;
        const r = await executeJson(params, hms);
        if (r.data && r.data.success) {
          this.modal = false;
          if (this.formType === 'dm')   await this.loadDm();
          if (this.formType === 'ca')   await this.loadCa();
          if (this.formType === 'dial') await this.loadDial();
          if (this.formType === 'nut')  await this.loadNut();
        } else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    async openDialDetail(c) {
      try {
        const r = await executeJson({ function: 'getDialysisDetail', dialysis_id: c.dialysis_id }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.dial.detail = { client: d.Client, sessions: d.Sessions || [] };
        }
      } catch (e) { console.error(e); }
    },
    blankSession() {
      const today = new Date().toISOString().substring(0,10);
      return { session_date: today, start_time: '08:00', end_time: '12:00', duration_minutes: 240, pre_weight: null, post_weight: null, fluid_removed: null, nurse_id: null, complications: '', status: 'completed', next_session_date: '' };
    },
    openSessionModal() { this.session = this.blankSession(); this.sessionModal = true; },
    async saveSession() {
      if (!this.dial.detail.client) return;
      this.saving = true;
      try {
        const r = await executeJson({ function: 'recordDialysisSession', dialysis_id: this.dial.detail.client.dialysis_id, ...this.session }, hms);
        if (r.data && r.data.success) { this.sessionModal = false; await this.openDialDetail(this.dial.detail.client); await this.loadDial(); }
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    // ---- formatting ----
    dmRow(c)   { if (c.last_hba1c >= 9) return 'row-action'; if (c.last_hba1c >= 7) return 'row-warn'; return ''; },
    caRow(c)   { if (c.stage === 'IV' || c.status === 'palliative') return 'row-warn'; if (c.status === 'deceased') return 'row-action'; return ''; },
    nutRow(c)  { if (c.admission_type === 'sam' && c.oedema == 1) return 'row-action'; if (c.admission_type === 'sam') return 'row-warn'; return ''; },

    hba1cClass(v) { if (v == null) return ''; if (v >= 9) return 'st-bad'; if (v >= 7) return 'st-warn'; return 'st-good'; },
    muacClass(v)  { if (v == null) return ''; if (v < 11.5) return 'st-bad'; if (v < 12.5) return 'st-warn'; return ''; },

    dmStatusPill(s) { return ({ active: 'hms-pill-info', controlled: 'hms-pill-success', uncontrolled: 'hms-pill-danger', lost: 'hms-pill-warn', deceased: 'hms-pill-muted', transferred: 'hms-pill-muted' })[s] || ''; },
    caStatusPill(s) { return ({ active: 'hms-pill-info', remission: 'hms-pill-success', recurrence: 'hms-pill-warn', palliative: 'hms-pill-warn', deceased: 'hms-pill-muted', lost: 'hms-pill-warn' })[s] || ''; },
    sessStatusPill(s) { return ({ completed: 'hms-pill-success', scheduled: 'hms-pill-info', in_progress: 'hms-pill-warn', missed: 'hms-pill-danger', cancelled: 'hms-pill-muted' })[s] || ''; },
    admissionPill(t) { return ({ sam: 'hms-pill-danger', mam: 'hms-pill-warn', at_risk: 'hms-pill-info' })[t] || ''; },
    nutStatusPill(s, e) { if (s === 'exited') return ({ recovered: 'hms-pill-success', died: 'hms-pill-danger', defaulted: 'hms-pill-warn', non_recovered: 'hms-pill-warn', transferred: 'hms-pill-muted' })[e] || 'hms-pill-muted'; return 'hms-pill-info'; },
    screenPill(s) { return ({ not_done: 'hms-pill-muted', normal: 'hms-pill-success', mild: 'hms-pill-info', moderate: 'hms-pill-warn', severe: 'hms-pill-danger', proliferative: 'hms-pill-danger', microalbuminuria: 'hms-pill-warn', macroalbuminuria: 'hms-pill-danger', ckd: 'hms-pill-danger' })[s] || 'hms-pill-muted'; },
    footPill(r) { return ({ low: 'hms-pill-success', moderate: 'hms-pill-warn', high: 'hms-pill-danger' })[r] || 'hms-pill-muted'; },
    stagePill(s) { return ({ '0': 'hms-pill-success', 'I': 'hms-pill-info', 'II': 'hms-pill-info', 'III': 'hms-pill-warn', 'IV': 'hms-pill-danger', 'unknown': 'hms-pill-muted' })[s] || 'hms-pill-muted'; }
  }
};
</script>

<style scoped>
.hms-page-body { padding: 16px 0 32px; }
.hms-back { color: #4a5568; font-size: 20px; text-decoration: none; }
.hms-page-title { font-size: 18px; font-weight: 600; color: #2d3748; }

.hms-disease-bar { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 16px; }
.hms-disease-btn { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 16px 14px; text-align: left; cursor: pointer; position: relative; transition: all .15s; }
.hms-disease-btn:hover { transform: translateY(-2px); border-color: #2b6cb0; }
.hms-disease-btn.active { border-color: #2b6cb0; background: #ebf8ff; box-shadow: 0 0 0 2px #2b6cb0; }
.hms-disease-btn .mdi { font-size: 24px; color: #2b6cb0; display: block; margin-bottom: 6px; }
.hms-disease-btn div { font-weight: 600; color: #2d3748; }
.hms-disease-count { position: absolute; top: 12px; right: 14px; background: #2b6cb0; color: #fff; padding: 2px 10px; border-radius: 10px; font-size: 12px; font-weight: 700; }

.hms-stat-row { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin-bottom: 16px; }
.hms-stat { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; text-align: center; }
.hms-stat-num { font-size: 26px; font-weight: 700; color: #1a202c; }
.hms-stat-lbl { font-size: 11px; color: #718096; text-transform: uppercase; letter-spacing: .5px; margin-top: 2px; }
.hms-stat-info { color: #2c5282; } .hms-stat-success { color: #276749; } .hms-stat-warning { color: #c05621; } .hms-stat-danger { color: #c53030; }

.hms-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; }
.hms-card-head { padding: 10px 14px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #2d3748; }
.hms-card-body { padding: 14px; }
.hms-card-body.p-0 { padding: 0; }

.hms-table { width: 100%; border-collapse: collapse; font-size: 13px; background: #fff; }
.hms-table th { background: #f7fafc; padding: 9px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #4a5568; text-align: left; }
.hms-table td { padding: 8px 9px; border-bottom: 1px solid #edf2f7; vertical-align: middle; }
.hms-table tr.row-warn   { background: #fffaf0; }
.hms-table tr.row-action { background: #fff5f5; }

.hms-input { width: 100%; padding: 6px 10px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 13px; }
.hms-label { font-size: 11px; font-weight: 600; color: #4a5568; display: block; margin-bottom: 3px; }

.hms-pill { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
.hms-pill-success { background: #c6f6d5; color: #22543d; }
.hms-pill-info    { background: #bee3f8; color: #2a4365; }
.hms-pill-warn    { background: #feebc8; color: #7b341e; }
.hms-pill-danger  { background: #fed7d7; color: #742a2a; }
.hms-pill-muted   { background: #edf2f7; color: #4a5568; }

.hms-modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,.5); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 20px; }
.hms-modal { background: #fff; border-radius: 8px; max-height: 92vh; display: flex; flex-direction: column; }
.hms-modal-head { padding: 14px 18px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
.hms-modal-body { padding: 16px 18px; overflow-y: auto; }
.hms-modal-foot { padding: 12px 18px; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 8px; }
.hms-icon-btn { background: none; border: none; cursor: pointer; }
.hms-patient-chip { display: inline-flex; align-items: center; gap: 4px; background: #ebf8ff; color: #2c5282; padding: 6px 10px; border-radius: 6px; font-size: 13px; }

.hms-btn { display: inline-flex; align-items: center; gap: 4px; padding: 7px 12px; border-radius: 6px; border: 1px solid transparent; font-size: 13px; font-weight: 500; cursor: pointer; }
.hms-btn-sm { padding: 4px 10px; font-size: 12px; }
.hms-btn-blue      { background: #2b6cb0; color: #fff; }
.hms-btn-success   { background: #38a169; color: #fff; }
.hms-btn-secondary { background: #fff; color: #2d3748; border: 1px solid #cbd5e0; }

.st-bad  { color: #c53030; font-weight: 700; }
.st-warn { color: #b45309; font-weight: 600; }
.st-good { color: #276749; font-weight: 600; }
.hms-muted { color: #a0aec0; }
.hms-muted-sm { color: #a0aec0; font-size: 11px; }
.hms-pad { padding: 24px; }
.ms-2 { margin-left: 8px; }
</style>
